import React, { useState } from "react";
import { Box, Typography } from "@mui/material";
import InfoOutlinedIcon from "@mui/icons-material/InfoOutlined";
import ModalShell from "./ModalShell";
import FileDropzone from "./FileDropzone";
import { collectionColors, font, subtitleSx } from "./tokens";

interface CreateFromConfigModalProps {
  open: boolean;
  onClose: () => void;
  onSubmit: (file: File) => void;
  isSubmitting?: boolean;
}

/**
 * Creates a collection from a configuration file alone.
 *
 * The difference from "New Collection": that one carries the survey YAML and the surveys
 * come into existence with the collection, while this file only names them by their
 * original_id — so they have to be uploaded already. The name, description, variant
 * labels and question labels all come from the file, which is why there is nothing to
 * fill in here.
 */
const CreateFromConfigModal: React.FC<CreateFromConfigModalProps> = ({
  open,
  onClose,
  onSubmit,
  isSubmitting = false,
}) => {
  const [file, setFile] = useState<File | null>(null);

  const close = () => {
    setFile(null);
    onClose();
  };

  return (
    <ModalShell
      open={open}
      onClose={close}
      title="Create from configuration file"
      subtitle="The file carries the collection's name, description and variants. Every survey it lists must already be uploaded to this organization."
      confirmLabel="Create collection"
      onConfirm={() => {
        if (file) {
          onSubmit(file);
        }
      }}
      confirmDisabled={!file}
      isSubmitting={isSubmitting}
    >
      <Box
        sx={{
          display: "flex",
          alignItems: "flex-start",
          gap: "10px",
          padding: "12px",
          background: collectionColors.surfaceLight,
          borderRadius: "8px",
        }}
      >
        <InfoOutlinedIcon
          sx={{ width: 16, height: 16, color: collectionColors.textSecondary, mt: "2px" }}
        />
        <Box>
          <Typography sx={{ ...subtitleSx, fontSize: "12px", lineHeight: "16px" }}>
            Expected shape
          </Typography>
          <Typography
            component="pre"
            sx={{
              fontFamily: "monospace",
              fontSize: "12px",
              lineHeight: "18px",
              color: collectionColors.textPrimary,
              margin: 0,
              whiteSpace: "pre-wrap",
            }}
          >
            {`collection_details:
  name: Healthcare Trust Survey
  description: ...
  surveys:
    - id: <survey original_id>
      display_label: English
  primary_survey_id: <survey original_id>`}
          </Typography>
          <Typography
            sx={{
              fontFamily: font,
              fontSize: "12px",
              lineHeight: "18px",
              color: collectionColors.textSecondary,
              marginTop: "6px",
            }}
          >
            Questions are optional; any the surveys no longer have are dropped rather than
            rejected.
          </Typography>
        </Box>
      </Box>

      <FileDropzone
        file={file}
        onSelect={setFile}
        placeholder="Click to upload or drag and drop"
      />
    </ModalShell>
  );
};

export default CreateFromConfigModal;
